import {
  Badge,
  Box,
  Button,
  Card,
  Field,
  HStack,
  Heading,
  InfoBox,
  Input,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  VStack,
  notify,
} from "components/ui";
import DeleteConfirmDialog from "components/common/DeleteConfirmDialog";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import { MdDelete, MdGroup, MdHistory } from "react-icons/md";
import {
  AccountMemory,
  CustomerCompanyDetail,
  customerCompanyService,
  UpdateCustomerCompanyRequest,
} from "services/customerCompanyService";
import { getThemeColor } from "theme/colors";
import { formatDate } from "./format";

interface OverviewTabProps {
  detail: CustomerCompanyDetail;
  /** Bubble a saved/toggled company back up so the page header badge stays in sync. */
  onDetailChange: (detail: CustomerCompanyDetail) => void;
}

/**
 * Company profile: headline counts, the staff-only active switch, the editable settings form and
 * the account memory shared across every customer of this company.
 */
const OverviewTab = ({ detail, onDetailChange }: OverviewTabProps) => {
  const { t } = useTranslation();

  const [form, setForm] = useState<UpdateCustomerCompanyRequest>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [deactivateOpen, setDeactivateOpen] = useState(false);

  const [memories, setMemories] = useState<AccountMemory[]>([]);
  const [memoriesLoading, setMemoriesLoading] = useState(false);
  const [memoriesError, setMemoriesError] = useState<string | null>(null);
  const [newMemoryContent, setNewMemoryContent] = useState("");
  const [addingMemory, setAddingMemory] = useState(false);
  const [deletingMemoryId, setDeletingMemoryId] = useState<string | null>(null);

  useEffect(() => {
    setForm({
      name: detail.name,
      externalRef: detail.externalRef ?? "",
      allowSelfRegistration: detail.allowSelfRegistration,
      defaultCredentialType: detail.defaultCredentialType,
      agentInstruction: detail.agentInstruction ?? "",
    });
  }, [detail]);

  useEffect(() => {
    let cancelled = false;
    const loadMemories = async () => {
      try {
        setMemoriesLoading(true);
        setMemoriesError(null);
        const result = await customerCompanyService.listAccountMemories(
          detail.id,
        );
        if (!cancelled) setMemories(result);
      } catch (err: any) {
        if (cancelled) return;
        setMemoriesError(
          err.response?.data?.error ||
            t("app.customerCompanies.memory.loadFailed", {
              defaultValue: "Failed to load account memories.",
            }),
        );
      } finally {
        if (!cancelled) setMemoriesLoading(false);
      }
    };
    void loadMemories();
    return () => {
      cancelled = true;
    };
  }, [detail.id, t]);

  const handleSave = async () => {
    if (!form.name?.trim()) {
      setError(
        t("app.customerCompanies.nameRequired", {
          defaultValue: "Name is required.",
        }),
      );
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload: UpdateCustomerCompanyRequest = {
        name: form.name?.trim(),
        externalRef: form.externalRef?.trim() || null,
        allowSelfRegistration: form.allowSelfRegistration,
        defaultCredentialType: form.defaultCredentialType,
        agentInstruction: form.agentInstruction?.trim() || null,
      };
      onDetailChange(await customerCompanyService.update(detail.id, payload));
      notify({
        type: "success",
        title: t("app.customerCompanies.saved", {
          defaultValue: "Customer company saved.",
        }),
      });
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t("app.errors.requestFailed", { defaultValue: "Request failed" }),
      );
    } finally {
      setSaving(false);
    }
  };

  const applyToggleActive = async (next: boolean) => {
    try {
      onDetailChange(
        await customerCompanyService.toggleActive(detail.id, next),
      );
      notify({
        type: "success",
        title: next
          ? t("app.customerCompanies.activated", {
              defaultValue: "Customer company activated.",
            })
          : t("app.customerCompanies.deactivated", {
              defaultValue: "Customer company deactivated.",
            }),
      });
    } catch (err: any) {
      notify({
        type: "error",
        title: t("app.errors.requestFailed", {
          defaultValue: "Request failed",
        }),
        description: err.response?.data?.error,
      });
    }
  };

  const handleToggleActive = (next: boolean) => {
    if (next) {
      void applyToggleActive(true);
      return;
    }
    setDeactivateOpen(true);
  };

  const handleAddMemory = async () => {
    const content = newMemoryContent.trim();
    if (!content) return;
    setAddingMemory(true);
    try {
      const created = await customerCompanyService.createAccountMemory(
        detail.id,
        { content },
      );
      setMemories((prev) => [created, ...prev]);
      setNewMemoryContent("");
      notify({
        type: "success",
        title: t("app.customerCompanies.memory.added", {
          defaultValue: "Memory added.",
        }),
      });
    } catch (err: any) {
      notify({
        type: "error",
        title: t("app.errors.requestFailed", {
          defaultValue: "Request failed",
        }),
        description: err.response?.data?.error,
      });
    } finally {
      setAddingMemory(false);
    }
  };

  const handleDeleteMemory = async (memoryId: string) => {
    setDeletingMemoryId(memoryId);
    try {
      await customerCompanyService.deleteAccountMemory(detail.id, memoryId);
      setMemories((prev) => prev.filter((m) => m.id !== memoryId));
      notify({
        type: "success",
        title: t("app.customerCompanies.memory.deleted", {
          defaultValue: "Memory deleted.",
        }),
      });
    } catch (err: any) {
      notify({
        type: "error",
        title: t("app.errors.requestFailed", {
          defaultValue: "Request failed",
        }),
        description: err.response?.data?.error,
      });
    } finally {
      setDeletingMemoryId(null);
    }
  };

  const cardProps = {
    borderWidth: "1px",
    borderColor: getThemeColor("card.border"),
    borderRadius: "md",
  } as const;

  return (
    <Stack gap={4}>
      {error && (
        <InfoBox variant="error">
          <Text color="red.700">{error}</Text>
        </InfoBox>
      )}

      {/* Aggregates */}
      <HStack gap={3} flexWrap="wrap">
        <Card.Root {...cardProps} flex="1" minW="200px">
          <Card.Body>
            <HStack gap={3}>
              <MdGroup size={32} color="var(--text-secondary-color)" />
              <Stack gap={0}>
                <Text fontSize="2xl" fontWeight="bold">
                  {detail.customerCount}
                </Text>
                <Text fontSize="sm" color="var(--text-secondary-color)">
                  {t("app.customerCompanies.aggregates.customers", {
                    defaultValue: "Customers",
                  })}
                </Text>
              </Stack>
            </HStack>
          </Card.Body>
        </Card.Root>

        <Card.Root {...cardProps} flex="1" minW="200px">
          <Card.Body>
            <HStack gap={3}>
              <MdGroup size={32} color="var(--text-secondary-color)" />
              <Stack gap={0}>
                <Text fontSize="2xl" fontWeight="bold">
                  {detail.customerAdminCount}
                </Text>
                <Text fontSize="sm" color="var(--text-secondary-color)">
                  {t("app.customerCompanies.aggregates.admins", {
                    defaultValue: "Customer admins",
                  })}
                </Text>
              </Stack>
            </HStack>
          </Card.Body>
        </Card.Root>

        <Card.Root {...cardProps} flex="1" minW="200px">
          <Card.Body>
            <HStack gap={3}>
              <MdHistory size={32} color="var(--text-secondary-color)" />
              <Stack gap={0}>
                <Text fontSize="md" fontWeight="bold">
                  {formatDate(detail.lastActivityAt)}
                </Text>
                <Text fontSize="sm" color="var(--text-secondary-color)">
                  {t("app.customerCompanies.aggregates.lastActivity", {
                    defaultValue: "Last activity",
                  })}
                </Text>
              </Stack>
            </HStack>
          </Card.Body>
        </Card.Root>
      </HStack>

      {/* IsActive control — staff-only, never editable from the Customer Portal */}
      <Card.Root {...cardProps}>
        <Card.Body>
          <HStack justify="space-between">
            <Stack gap={1}>
              <Text fontWeight="medium">
                {t("app.customerCompanies.activeToggle.title", {
                  defaultValue: "Active",
                })}
              </Text>
              <Text fontSize="sm" color="var(--text-secondary-color)">
                {t("app.customerCompanies.activeToggle.help", {
                  defaultValue:
                    "When off, all customers of this company are blocked from authenticating and their active sessions are revoked.",
                })}
              </Text>
            </Stack>
            <Switch
              checked={detail.isActive}
              onCheckedChange={(e: any) => handleToggleActive(e.checked)}
            />
          </HStack>
        </Card.Body>
      </Card.Root>

      {/* Editable settings */}
      <Card.Root {...cardProps}>
        <Card.Body>
          <Stack gap={4}>
            <Heading size="sm">
              {t("app.customerCompanies.sections.settings", {
                defaultValue: "Settings",
              })}
            </Heading>

            <Field
              label={t("app.customerCompanies.fields.name", {
                defaultValue: "Name",
              })}
              required
            >
              <Input
                value={form.name ?? ""}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
                maxLength={255}
              />
            </Field>

            <Field
              label={t("app.customerCompanies.fields.externalRef", {
                defaultValue: "Company ID (optional)",
              })}
              helperText={t("app.customerCompanies.fields.externalRefHelp", {
                defaultValue:
                  "Stable external identifier. Auto-populated from the widget's userCompanyId on first chat; can be edited.",
              })}
            >
              <Input
                value={form.externalRef ?? ""}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, externalRef: e.target.value }))
                }
                maxLength={255}
              />
            </Field>

            <Box>
              <HStack justify="space-between">
                <Stack gap={1}>
                  <Text fontWeight="medium">
                    {t("app.customerCompanies.fields.allowSelfRegistration", {
                      defaultValue: "Allow self-registration",
                    })}
                  </Text>
                  <Text fontSize="sm" color="var(--text-secondary-color)">
                    {t(
                      "app.customerCompanies.fields.allowSelfRegistrationHelp",
                      {
                        defaultValue:
                          "When enabled, anyone with the registration URL can register against this company.",
                      },
                    )}
                  </Text>
                </Stack>
                <Switch
                  checked={form.allowSelfRegistration ?? false}
                  onCheckedChange={(e: any) =>
                    setForm((prev) => ({
                      ...prev,
                      allowSelfRegistration: e.checked,
                    }))
                  }
                />
              </HStack>
            </Box>

            <Select
              label={t("app.customerCompanies.fields.defaultCredentialType", {
                defaultValue: "Default credential type (hint)",
              })}
              value={
                form.defaultCredentialType === null ||
                form.defaultCredentialType === undefined
                  ? [""]
                  : [String(form.defaultCredentialType)]
              }
              onValueChange={({ value }) => {
                const v = value[0] ?? "";
                setForm((prev) => ({
                  ...prev,
                  defaultCredentialType: v === "" ? null : Number(v),
                }));
              }}
              items={[
                {
                  value: "",
                  label: t("app.customerCompanies.credentialNoHint", {
                    defaultValue: "No hint (let customer choose)",
                  }),
                },
                {
                  value: "1",
                  label: t("app.customerCompanies.credentialPin", {
                    defaultValue: "PIN (4-6 digits)",
                  }),
                },
                {
                  value: "2",
                  label: t("app.customerCompanies.credentialPassword", {
                    defaultValue: "Password",
                  }),
                },
              ]}
            />

            <Field
              label={t("app.customerCompanies.fields.agentInstruction", {
                defaultValue: "Agent instruction (optional)",
              })}
              helperText={t(
                "app.customerCompanies.fields.agentInstructionHelp",
                {
                  defaultValue:
                    "Free-text guidance merged into the agent prompt at runtime, alongside the tenant-global instructions.",
                },
              )}
            >
              <Textarea
                value={form.agentInstruction ?? ""}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    agentInstruction: e.target.value,
                  }))
                }
                rows={6}
              />
            </Field>

            <HStack justify="flex-end" gap={3}>
              <Button intent="primary" onClick={handleSave} disabled={saving}>
                {saving
                  ? t("app.common.saving", { defaultValue: "Saving..." })
                  : t("app.common.save")}
              </Button>
            </HStack>
          </Stack>
        </Card.Body>
      </Card.Root>

      {/* Account Memory — facts shared across every customer of this company */}
      <Card.Root {...cardProps}>
        <Card.Body>
          <Stack gap={4}>
            <Stack gap={1}>
              <Heading size="sm">
                {t("app.customerCompanies.memory.title", {
                  defaultValue: "Account Memory",
                })}
              </Heading>
              <Text fontSize="sm" color="var(--text-secondary-color)">
                {t("app.customerCompanies.memory.subtitle", {
                  defaultValue:
                    "Facts the assistant remembers for every customer of this company.",
                })}
              </Text>
            </Stack>

            <Field
              label={t("app.customerCompanies.memory.addLabel", {
                defaultValue: "Add a memory",
              })}
              helperText={t("app.customerCompanies.memory.addHelp", {
                defaultValue:
                  "Free-text fact applied to all customers in this company at runtime.",
              })}
            >
              <Textarea
                value={newMemoryContent}
                onChange={(e) => setNewMemoryContent(e.target.value)}
                placeholder={t("app.customerCompanies.memory.placeholder", {
                  defaultValue: "e.g. This account is on the Enterprise plan.",
                })}
                rows={3}
              />
            </Field>

            <HStack justify="flex-end">
              <Button
                intent="primary"
                onClick={handleAddMemory}
                disabled={addingMemory || !newMemoryContent.trim()}
              >
                {addingMemory
                  ? t("app.customerCompanies.memory.adding", {
                      defaultValue: "Adding...",
                    })
                  : t("app.customerCompanies.memory.add", {
                      defaultValue: "Add memory",
                    })}
              </Button>
            </HStack>

            {memoriesError && (
              <InfoBox variant="error">
                <Text color="red.700">{memoriesError}</Text>
              </InfoBox>
            )}

            {memoriesLoading ? (
              <Text fontSize="sm" color="var(--text-secondary-color)">
                {t("app.customerCompanies.memory.loading", {
                  defaultValue: "Loading memories…",
                })}
              </Text>
            ) : memories.length === 0 ? (
              <Text fontSize="sm" color="var(--text-secondary-color)">
                {t("app.customerCompanies.memory.empty", {
                  defaultValue: "No account memories yet.",
                })}
              </Text>
            ) : (
              <Stack gap={2}>
                {memories.map((m) => (
                  <HStack
                    key={m.id}
                    justify="space-between"
                    align="flex-start"
                    gap={3}
                    borderWidth="1px"
                    borderColor={getThemeColor("card.border")}
                    borderRadius="md"
                    p={3}
                  >
                    <Stack gap={1} flex={1}>
                      <Text fontSize="sm">{m.content}</Text>
                      <HStack gap={2}>
                        <Badge variant="subtle">{m.category}</Badge>
                        <Text fontSize="xs" color="var(--text-secondary-color)">
                          {formatDate(m.createdAt)}
                        </Text>
                      </HStack>
                    </Stack>
                    <Button
                      intent="ghost"
                      onClick={() => handleDeleteMemory(m.id)}
                      disabled={deletingMemoryId === m.id}
                    >
                      <MdDelete />{" "}
                      {t("app.common.delete", { defaultValue: "Delete" })}
                    </Button>
                  </HStack>
                ))}
              </Stack>
            )}
          </Stack>
        </Card.Body>
      </Card.Root>

      {/* Timestamps footer */}
      <VStack align="flex-start" gap={1}>
        <Text fontSize="xs" color="var(--text-secondary-color)">
          {t("app.customerCompanies.createdAt", { defaultValue: "Created" })}:{" "}
          {formatDate(detail.createdAt)}
        </Text>
        <Text fontSize="xs" color="var(--text-secondary-color)">
          {t("app.customerCompanies.updatedAt", { defaultValue: "Updated" })}:{" "}
          {formatDate(detail.updatedAt)}
        </Text>
      </VStack>

      <DeleteConfirmDialog
        isOpen={deactivateOpen}
        onClose={() => setDeactivateOpen(false)}
        onConfirm={() => {
          setDeactivateOpen(false);
          void applyToggleActive(false);
        }}
        title={t("app.customerCompanies.activeToggle.title", {
          defaultValue: "Active",
        })}
        message={t("app.customerCompanies.confirmDeactivate", {
          defaultValue:
            "Deactivating this company signs its customers out, blocks them from logging in and skips their scheduled workflows until you reactivate it. Continue?",
        })}
      />
    </Stack>
  );
};

export default OverviewTab;
